/**
 * Seletor do banco em cujo contexto a sessão opera (E57.F2, ADR-0066). // ux-neutrality:allow doc-comment
 *
 * Só aparece para quem tem escolha: com um vínculo só, o servidor já entra // ux-neutrality:allow doc-comment
 * direto no contexto e o seletor seria pergunta sem resposta alternativa. // ux-neutrality:allow doc-comment
 * Com nenhum vínculo, nem existe. // ux-neutrality:allow doc-comment
 *
 * O que ele mostra é o que o SERVIDOR diz estar ativo (`perfil.banco_ativo`), // ux-neutrality:allow doc-comment
 * não o que o navegador guardou: o armazenamento local é uma proposta, e o // ux-neutrality:allow doc-comment
 * servidor confere contra o vínculo antes de aplicar. Se os dois divergirem, // ux-neutrality:allow doc-comment
 * quem manda é o servidor — senão a tela afirmaria um isolamento que o banco // ux-neutrality:allow doc-comment
 * de dados não está aplicando. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { listarBancosDoContexto, type BancoVinculo } from '@/lib/perfil';
import { useSessionStore } from '@/stores/session';

export interface BancoAtivoSelectorProps {
  /** Contexto vigente segundo o servidor. `null` = ainda não escolhido. */
  bancoAtivo: string | null;
}

export function BancoAtivoSelector({ bancoAtivo }: BancoAtivoSelectorProps) {
  const token = useSessionStore((s) => s.token); // ux-neutrality:allow auth-token
  const setBancoAtivo = useSessionStore((s) => s.setBancoAtivo);
  const apiClient = useApiClient();
  const [vinculos, setVinculos] = useState<BancoVinculo[]>([]);

  useEffect(() => {
    if (!token /* ux-neutrality:allow auth-token */) {
      setVinculos([]);
      return;
    }
    let cancelado = false;
    listarBancosDoContexto(apiClient)
      .then((lista) => {
        if (!cancelado) setVinculos(lista);
      })
      .catch(() => {
        if (!cancelado) setVinculos([]);
      });
    return () => {
      cancelado = true;
    };
  }, [apiClient, token]); // ux-neutrality:allow auth-token

  const trocar = useCallback(
    (valor: string) => {
      setBancoAtivo(valor === '' ? null : valor);
      // Recarrega para que TODA leitura em tela seja refeita sob o contexto
      // novo. Sem isso, listas já carregadas continuariam mostrando a carteira
      // anterior — tela velha sobre isolamento novo é pior que tela vazia.
      window.location.reload();
    },
    [setBancoAtivo],
  );

  if (vinculos.length < 2) return null;

  const semContexto = bancoAtivo === null;
  return (
    <div className="flex flex-col gap-1 px-1 py-1.5" data-testid="banco-ativo-selector">
      <label
        className="text-[10px] uppercase tracking-wide text-fg-muted"
        htmlFor="banco-ativo-select"
      >
        {ptBR.shell.bancoAtivo.label}
      </label>
      <select
        id="banco-ativo-select"
        data-testid="banco-ativo-select"
        value={bancoAtivo ?? ''}
        onChange={(e) => trocar(e.target.value)}
        className="w-full border border-border bg-surface-2 px-2 py-1 text-[12.5px] text-fg focus-visible:outline-2 focus-visible:outline-accent"
      >
        <option value="">{ptBR.shell.bancoAtivo.placeholder}</option>
        {vinculos.map((v) => (
          <option key={v.id} value={v.id}>
            {v.nome}
          </option>
        ))}
      </select>
      {semContexto ? (
        <p className="text-[10px] leading-tight text-fg-muted" data-testid="banco-ativo-aviso">
          {ptBR.shell.bancoAtivo.semContexto}
        </p>
      ) : null}
    </div>
  );
}
